/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import { useNavigate, useParams } from 'react-router-dom'
import { doc, collection, getDoc, updateDoc, DocumentSnapshot, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '../../firebase'
import { Box, Typography, Table, TableBody, TableHead, TableRow, TableCell, TableContainer, Paper, Backdrop, CircularProgress, Stack, Fab, Tabs, Tab, TablePagination, TextField } from "@mui/material"
import FormElement from "../lib/FormElement"
import isEmpty from 'lodash.isempty'
import { validate } from "../lib/FormValidation"
import parse from 'html-react-parser'
import { toast } from 'react-toastify'
import dayjs from "dayjs"

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const CustomerDetails = () => {
  const urlParams = useParams<{ customerId: string }>()
  const navigate = useNavigate()
  const [forms, setForms] = useState<any>()
  const [formValues, setFormValues] = useState<any>()
  const [error, setError] = useState<string>('')
  const [prosessing, setProcessing] = useState<boolean>(false)
  const [tabValue, setTabValue] = useState(0)
  const [shippedProducts, setShippedProducts] = useState<any[]>([])
  const [filteredProducts, setFilteredProducts] = useState<any[]>([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [startDate, setStartDate] = useState(() => dayjs().subtract(1, 'year').format('YYYY-MM-DD'))
  const [endDate, setEndDate] = useState(() => dayjs().format('YYYY-MM-DD'))

  useEffect(() => {
    getDoc(doc(collection(db, 'forms'), 'customer')).then((_: DocumentSnapshot) => {
      _.exists() && setForms(_.data().conf)
    })
  }, [urlParams])

  useEffect(() => {
    if (!forms) return
    const unsubscribe = onSnapshot(doc(collection(db, 'customers'), urlParams.customerId), (_: DocumentSnapshot) => {
      const defaultValues = forms.reduce((a: any, b: any) => {
        a[b.name] = ""
        return a
      }, {})
      if(!_.exists()) return navigate('/noData')
      const data = _.data()
      const mergedValues = { ...defaultValues, ...data }
      setFormValues(mergedValues)

      // 顧客情報取得後に出荷商品を取得
      if (data?.companyName) {
        const q = query(
          collection(db, 'products'),
          where('customer', '==', data.companyName),
          where('isSold', '==', true)
        )

        onSnapshot(q, (snapshot) => {
          const products = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          setShippedProducts(products)
        })
      }
    })
    return unsubscribe
  }, [forms])

  const onChange = ({name, value}) => {
    setFormValues({...formValues, [name]: value})
  }
  const update = async () => {
    const errors = forms.map((form: any) => {
      return validate(formValues[form.name], form.label, form.validation)
    })
    const errorMessages = errors.filter((e: any) => !isEmpty(e)).join("<br/>")
    if (!isEmpty(errorMessages)) {
      setError(errorMessages)
      return
    }
    setProcessing(true)
    updateDoc(doc(collection(db, 'customers'), urlParams.customerId),
      { ...formValues, updatedAt: dayjs(new Date()).format('YYYY-MM-DD HH:mm') })
      .then(() => {
        toast.success("狩猟者情報を更新しました")
        setError("")
      }).catch(e => {
        toast.error(e)
      }).finally(() => {
        setProcessing(false)
      })
  }

  const getValue = (name: string, type?: string) => {
    switch (type) {
      case "upload":
        return formValues[name].data
      default:
        return formValues[name]
    }
  }

  const changeState = (isActive: boolean) => {
    setProcessing(true)
    updateDoc(doc(collection(db, 'customers'), urlParams.customerId),
      { ...formValues, updatedAt: dayjs(new Date()).format('YYYY-MM-DD HH:mm'), status: isActive ? "active" : "suspended" })
      .then(() => {
        toast.success("取引先情報を更新しました")
        setError("")
      }).catch(e => {
        toast.error(e)
      }).finally(() => {
        setProcessing(false)
      })
  }

  // 日付範囲フィルター処理
  useEffect(() => {
    if (!shippedProducts.length) {
      setFilteredProducts([])
      return
    }

    const filtered = shippedProducts.filter(product => {
      if (!product.shippingDate) return false

      const shippingDate = dayjs(product.shippingDate, 'YYYY/MM/DD')
      const start = dayjs(startDate)
      const end = dayjs(endDate)

      return (shippingDate.isAfter(start) || shippingDate.isSame(start)) &&
             (shippingDate.isBefore(end) || shippingDate.isSame(end))
    })

    setFilteredProducts(filtered)
    setPage(0) // フィルター変更時はページを最初に戻す
  }, [shippedProducts, startDate, endDate])

  // ページング関連の関数
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // 現在のページに表示する商品を取得
  const paginatedProducts = filteredProducts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <Box
        m={2}
        width="80%"
        height="80vh"
        display="flex-col"
        mt={5}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label="顧客情報" />
            <Tab label="出荷商品リスト" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <TableContainer component={Paper}>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="left">項目</TableCell>
                  <TableCell width="20%" align="left">入力内容</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ background: formValues && ["registed", "suspended"].includes(formValues["status"]) ? "silver" : "white" }}>
                {
                  forms && formValues && forms.map((form: any) => (
                    <TableRow
                      key={form.name}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {form.label}{form.type === 'checkbox' ? ` (${form.title})` : ""}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <FormElement
                            type={form.type}
                            name={form.name}
                            value={getValue(form.name)}
                            onChange={onChange}
                            options={form.options}
                            text={form.text}
                            // disabled={formDisabled}
                          />
                      </TableCell>
                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>
          </TableContainer>
          <Box mt={2} display="flex" justifyContent="center">
            <Typography variant="body2" color="red">{parse(error)}</Typography>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box mb={2}>
            <Typography variant="h6" mb={2}>
              出荷商品リスト (全{shippedProducts.length}件 / 表示{filteredProducts.length}件)
            </Typography>
            <Box display="flex" gap={2} mb={2}>
              <TextField
                label="開始日"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
              />
              <TextField
                label="終了日"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
              />
            </Box>
          </Box>
          {filteredProducts.length > 0 ? (
            <Paper>
              <TableContainer>
                <Table aria-label="shipped products table">
                  <TableHead>
                    <TableRow>
                      <TableCell>製品番号</TableCell>
                      <TableCell>個体番号</TableCell>
                      <TableCell>種類</TableCell>
                      <TableCell>部位</TableCell>
                      <TableCell>重量</TableCell>
                      <TableCell>処理日</TableCell>
                      <TableCell>消費期限</TableCell>
                      <TableCell>出荷日</TableCell>
                      <TableCell>出荷メモ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedProducts.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>{product.productNo}</TableCell>
                        <TableCell>{product.individualNo}</TableCell>
                        <TableCell>{product.type}</TableCell>
                        <TableCell>{product.part}</TableCell>
                        <TableCell>{product.weight}kg</TableCell>
                        <TableCell>{product.processingDate}</TableCell>
                        <TableCell>{product.expirationDate}</TableCell>
                        <TableCell>{product.shippingDate}</TableCell>
                        <TableCell>{product.shippingMemo}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredProducts.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="1ページあたりの行数:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
              />
            </Paper>
          ) : (
            <Typography variant="body1" color="text.secondary">
              この顧客への出荷商品はありません。
            </Typography>
          )}
        </TabPanel>

        <Box mt={2} display="flex" justifyContent="center">
          <Stack direction="row" spacing={3}>
            {
              formValues && ["registed", "suspended"].includes(formValues["status"]) ?
                <Fab variant="extended" color="warning" onClick={() => changeState(true)} style={{ right: 20, bottom: 80, position: "fixed" }}>&emsp;有&emsp;効&emsp;化&emsp;</Fab> :
                <Fab variant="extended" color="success" onClick={() => changeState(false)} style={{ right: 20, bottom: 80, position: "fixed" }}>&emsp;無&emsp;効&emsp;化&emsp;</Fab>
            }
            <Fab variant="extended" color="info" onClick={update} style={{ right: 20, bottom: 20, position: "fixed" }}>&emsp;更&emsp;&emsp;新&emsp;</Fab>
          </Stack>
        </Box>
      </Box>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={prosessing}
        onClick={() => setProcessing(false)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  )
}
export default CustomerDetails
